import cookieService from 'services/cookie-service'
import { getSingleParamFromUrl } from 'utils/urls'

export type Language = 'de' | 'pl' | 'ru' | 'uk' | 'en' | 'sk' | 'ro' | 'it' | 'fr' | 'es' | 'cs'

export enum SupportedLanguages {
  pl = 'pl',
  en = 'en',
  ru = 'ru',
  uk = 'uk',
  de = 'de',
  sk = 'sk',
  it = 'it',
  ro = 'ro',
  cs = 'cs',
  es = 'es',
  fr = 'fr',
}

export const DEFAULT_LANGUAGE: Language = SupportedLanguages.pl

const isBrowser = typeof window !== 'undefined'

export const isSupportedLanguage = (lang: unknown): lang is Language =>
  typeof lang === 'string' && (Object.values(SupportedLanguages) as string[]).includes(lang)

export const getLangFromCookie = (): Language | null => {
  const cookieLang = cookieService.getItem('language')
  return isSupportedLanguage(cookieLang) ? cookieLang : null
}

export const getLangFromUrl = (pathname?: string): Language => {
  const url = pathname || window.location.pathname
  let lang: Language = 'pl'
  Object.values(SupportedLanguages).forEach((supportedLang) => {
    if (url.includes(`/${supportedLang}/`) || url === `/${supportedLang}`) {
      lang = supportedLang as Language
    }
  })
  return lang
}

export const setCookieLangFromUrl = (): void => {
  const urlLang = String(getLangFromUrl()).toLowerCase()
  cookieService.setItem('language', urlLang, cookieService.maximalCookieTTL)
}

export const getLangFromUrlParam = (): Language | null => {
  let langParam = (getSingleParamFromUrl('lang') || getSingleParamFromUrl('language')) as string | null
  if (langParam === 'ua') langParam = 'uk' // ua is another name for uk
  return isSupportedLanguage(langParam) ? langParam : null
}

export const getServerLanguage = (): Language => {
  const langFromEnv = process?.env?.LANGUAGE
  return isSupportedLanguage(langFromEnv) ? langFromEnv : DEFAULT_LANGUAGE
}

export const getBrowserLanguage = (): Language => {
  const firstSeg = window.location.pathname.split('/').filter(Boolean)[0]
  return isSupportedLanguage(firstSeg) ? firstSeg : DEFAULT_LANGUAGE
}

const initialLanguage: Language = isBrowser ? getBrowserLanguage() : getServerLanguage()

export function getCurrentLanguage(): Language {
  if (!isBrowser) return initialLanguage
  return getLangFromUrlParam() ?? initialLanguage
}
